// src/pages/PositionsPage.jsx — "Your Philosophy, So Far"
// The reader's answers at the canonical forks, assembled into a living record:
// each position with its lens, the named tensions between answers worth
// sitting with, and a copyable summary. Reading becomes self-discovery.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Compass, AlertTriangle, Copy, Check, ArrowRight, Lock } from 'lucide-react';
import { POSITION_POINTS, evaluateTensions } from '../data/positionPoints';
import { usePositions } from '../hooks/usePositions';
import { usePositionAggregate } from '../hooks/useReaderSignals';
import WhereReadersLand from '../components/modules/WhereReadersLand';
import { getAllModules } from '../data/modules';

// Hook-per-fork wrapper (hooks can't live inside the map below).
const PositionCrowd = ({ point, choice }) => {
  const aggregate = usePositionAggregate(point.id, choice);
  return <WhereReadersLand point={point} aggregate={aggregate} ownChoice={choice} compact />;
};

const PositionsPage = () => {
  const { positions, answeredCount } = usePositions();
  const [copied, setCopied] = useState(false);

  const modulesByOrder = useMemo(() => {
    const map = {};
    getAllModules().forEach(m => { map[m.order] = m; });
    return map;
  }, []);

  const grouped = useMemo(() => {
    const groups = {};
    POSITION_POINTS.forEach(p => {
      groups[p.module] = groups[p.module] || [];
      groups[p.module].push(p);
    });
    return groups;
  }, []);

  const tensions = evaluateTensions(positions);

  const copySummary = () => {
    const lines = ['My philosophy, so far (Profound Philosophy)', ''];
    POSITION_POINTS.forEach(p => {
      const a = positions[p.id];
      if (a) {
        const opt = p.options.find(o => o.id === a.choice);
        lines.push(`${p.title}: ${opt ? opt.label : a.choice}`);
      }
    });
    if (tensions.length > 0) {
      lines.push('', 'Worth sitting with:');
      tensions.forEach(t => lines.push(`- ${t.name}`));
    }
    navigator.clipboard?.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal via-gunmetal to-charcoal pt-28 pb-16">
      {/* One small lit cloud in a vast sky: your positions, choosing their shape */}
      <div className="relative h-44 sm:h-56 overflow-hidden mb-8" aria-hidden="true">
        <img
          src="/images/cloud-08.webp"
          alt=""
          className="w-full h-full object-cover object-[65%_30%]"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-transparent to-charcoal" />
      </div>
      <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <Compass className="w-8 h-8 text-violet-400" />
            <h1 className="text-3xl sm:text-4xl font-bold text-white">Your Philosophy, So Far</h1>
          </div>
          <p className="text-slate-300 mb-1">
            {answeredCount === 0
              ? 'No positions taken yet. The forks are waiting inside the modules.'
              : `You have taken ${answeredCount} of ${POSITION_POINTS.length} positions. Every answer can change; philosophers change theirs constantly, for reasons. That is the discipline working.`}
          </p>
          {answeredCount > 0 && (
            <button
              onClick={copySummary}
              aria-live="polite"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-violet-500/40 text-violet-200 hover:bg-violet-500/10 transition-colors text-sm"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy my summary'}
            </button>
          )}
        </motion.div>

        {tensions.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Worth Sitting With
            </h2>
            <div className="space-y-3">
              {tensions.map(t => (
                <div key={t.id} className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
                  <h3 className="font-bold text-amber-200 mb-1">{t.name}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{t.insight}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 space-y-8">
          {Object.entries(grouped).map(([order, points]) => {
            const mod = modulesByOrder[Number(order)];
            return (
              <div key={order}>
                <h2 className="text-sm font-semibold tracking-wide uppercase text-slate-400 mb-3">
                  Module {order} · {mod ? mod.title : ''}
                </h2>
                <div className="space-y-3">
                  {points.map(p => {
                    const a = positions[p.id];
                    const opt = a && p.options.find(o => o.id === a.choice);
                    return (
                      <div
                        key={p.id}
                        className={`rounded-xl border p-5 ${
                          a ? 'border-violet-500/30 bg-violet-500/5' : 'border-slate-700 bg-slate-800/30'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="font-bold text-white">{p.title}</h3>
                            {a && opt ? (
                              <>
                                <p className="text-violet-200 mt-1">{opt.label}</p>
                                <p className="text-sm text-slate-400 mt-1">{opt.lens}</p>
                                <PositionCrowd point={p} choice={a.choice} />
                              </>
                            ) : (
                              <p className="text-sm text-slate-400 mt-1 flex items-center gap-2">
                                <Lock className="w-4 h-4" />
                                Waiting for you in Module {p.module}
                                {mod && (
                                  <Link
                                    to={`/modules/${mod.id}`}
                                    className="inline-flex items-center gap-1 text-violet-300 hover:text-white transition-colors"
                                  >
                                    Go there <ArrowRight className="w-3 h-3" />
                                  </Link>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-400 mt-10">
          Positions are stored on this device. The "where readers land" figures are anonymous
          counts of how readers across the app answered: no names, no comments, just how the
          room split. Your own answer joins the count the moment you take it, and moves with
          you when you change your mind.
        </p>
      </div>
    </div>
  );
};

export default PositionsPage;
